import { isGlobalAdvisorTurn, resolveMultiProjectMentions } from '@/lib/engaged-projects';
import type { ProjectRef } from '@/lib/board-route';

const VISIT_GOAL_KINDS = new Set([
  'visit_ask',
  'visit_propose',
  'propose_visit',
  'visit_booked',
  'visit_clash',
]);

export function isVisitSchedulingGoal(goalKind?: string): boolean {
  return !!goalKind && VISIT_GOAL_KINDS.has(goalKind);
}

const VISIT_BOARD_QUESTION_RE =
  /\b(?:legal|title|rera|amenit|facilit|price|pricing|emi|compare|budget|which fits|starting price|khata)\b/i;

const VISIT_SCHEDULE_RE =
  /\b(?:saturd|sun(day)?|mon(day)?|tue(sday)?|wed(nesday)?|thu(rsday)?|fri(day)?|tomorrow|weekend|morning|afternoon|evening|works for me|that works|this week|next week|same\s+day|that\s+day|that\s+date|\d{1,2}\s*(?::\d{2})?\s*[ap]m|\d{1,2}\s*jul)\b/i;

/** Buyer asks about another stop after a visit was booked ("what about cornerstone?"). */
export function isVisitFollowUpTurn(text: string): boolean {
  return /\bwhat about\b/i.test(text.trim());
}

/** Buyer adds another project to an in-progress visit route. */
export function isVisitRouteExpandTurn(text: string): boolean {
  return /\b(?:add|include|also|too|as well)\b/i.test(text.trim());
}

/** Multi-project visit intent from chat ("visit eldorado and cornerstone"). */
export function isMultiVisitRequestTurn(text: string, projects: readonly ProjectRef[]): boolean {
  if (/\b(?:would like to|want to|like to)\s+(?:to\s+)?visit\b/i.test(text)) return true;
  if (/\bplan\s+(?:a\s+)?visits?\s+to\b/i.test(text)) return true;
  return /\bvisit\b/i.test(text) && resolveMultiProjectMentions(text, projects).length >= 2;
}

/** Date/time/confirm language on the visit board — handled locally, not as a project Q&A turn. */
export function isVisitBoardSchedulingTurn(text: string): boolean {
  const t = text.trim();
  if (!t || isGlobalAdvisorTurn(t)) return false;
  if (VISIT_BOARD_QUESTION_RE.test(t)) return false;
  if (VISIT_SCHEDULE_RE.test(t)) return true;
  if (/^\s*(?:yes|yeah|yep|ok(?:ay)?|sure|confirm(?:ed)?|go ahead|perfect|great|done)\s*\.?$/i.test(t)) {
    return true;
  }
  return false;
}

export interface VisitDateOption {
  id: string;
  label: string;
  /** Text sent to chat when chip tapped — defaults to weekday name. */
  chatText?: string;
}

/** Next N calendar days for live-mode visit board (any day, not Saturdays only). */
export function buildLiveVisitCalendar(count = 14): VisitDateOption[] {
  const out: VisitDateOption[] = [];
  const base = new Date();
  for (let i = 1; i <= count; i++) {
    const d = new Date(base);
    d.setDate(base.getDate() + i);
    const weekday = d.toLocaleDateString('en-IN', { weekday: 'long' });
    const label = d.toLocaleDateString('en-IN', { weekday: 'short', day: 'numeric', month: 'short' });
    out.push({ id: `live-d-${i}`, label, chatText: weekday });
  }
  return out;
}

/** Clock time from server `proposed_label` (e.g. "Monday at 11:00 AM" → "11:00 AM"). */
export function timeFromProposedLabel(label: string): string | null {
  const m = label.match(/(\d{1,2}(?::\d{2})?\s*[AP]M)/i);
  return m?.[1] ?? null;
}

export function resolveVisitDateFromText(
  text: string,
  dates: readonly VisitDateOption[],
): string | null {
  const t = text.toLowerCase();
  for (const d of dates) {
    if (t.includes(d.label.toLowerCase())) return d.id;
    const labelDay = /(\d{1,2})\s*jul/i.exec(d.label);
    const textDay = /(\d{1,2})\s*jul/i.exec(text);
    if (labelDay && textDay && labelDay[1] === textDay[1]) return d.id;
  }
  if (/\bnext week\b/i.test(t) && dates[1]) return dates[1].id;
  if (/\b(?:this week|first saturday|saturd.*works|works.*saturd)\b/i.test(t)) {
    return dates[0]?.id ?? null;
  }
  if (/\b(?:saturd|weekend)\b/i.test(t)) return dates[0]?.id ?? null;
  return null;
}

export function visitBoardSchedulingReply(text: string, dateLabel?: string): string {
  if (dateLabel && /\bmorning|afternoon|evening\b/i.test(text)) {
    return `${dateLabel} noted — route is updated on the board. Tap **Reveal my number & confirm** when ready.`;
  }
  if (dateLabel) {
    return `${dateLabel} is selected on the board — check your route, then confirm when ready.`;
  }
  if (/\bmorning|afternoon|evening\b/i.test(text)) {
    return 'Got it — pick your Saturday on the board if you have not already, then confirm.';
  }
  return 'Continue on the visit board — pick a Saturday and confirm when the route looks right.';
}

/** Single ack when the user plans visits from the board — not N shortlist toasts. */
export function visitBoardPlanAck(projectNames: string[]): string {
  const n = projectNames.length;
  if (n === 0) return 'Pick projects on your matches board, then plan a visit day.';
  if (n === 1) {
    return `Visit day is open for ${projectNames[0]}. Pick a Saturday on the board, check your route, then confirm.`;
  }
  const preview = projectNames.slice(0, 3).join(', ');
  const extra = n > 3 ? ` (+${n - 3} more)` : '';
  return `${n} stops ready — ${preview}${extra}. Pick a Saturday on the board, then confirm when the route looks right.`;
}

/** Chat stays thin only in demo; live mode shows server copy (WhatsApp parity). */
export function chatReplyForBoardVisit(
  fullReply: string,
  goalKind?: string,
  mode: 'demo' | 'live' = 'live',
): string {
  if (mode === 'live') return fullReply.trim();
  if (goalKind === 'visit_booked') return fullReply.trim();
  if (!isVisitSchedulingGoal(goalKind)) return fullReply.trim();

  if (/which day works/i.test(fullReply)) {
    return 'Pick a Saturday on the visit board, then confirm when your route looks right.';
  }
  if (/shall i lock|reply yes to confirm/i.test(fullReply)) {
    return 'Almost there — review the route on the board and tap Reveal my number & confirm.';
  }
  if (/happy to set up a visit/i.test(fullReply)) {
    return 'Visit day is on your board — pick a Saturday and confirm there.';
  }

  return 'Continue on the visit board — pick a date and confirm when ready.';
}
